// Who can open a document or a source folder in SharePoint, measured against the people Ask HR
// answers ("Ask HR - Users"). Answers link to the SharePoint file itself, so a file only HR can
// open gives most employees SharePoint's "You need access" page from a citation. The hourly sync
// works this out (services/ingest/src/visibility.ts) and Admin shows it. Browser-safe: no Node.

export type VisibilityState = "everyone" | "link" | "some" | "unknown";

export interface Visibility {
  // everyone: (nearly) all of Ask HR - Users can open it. link: not, but an organization-wide
  // sharing link exists; that link only works for people who open it, not for the address answers
  // cite. some: only the people in `who`. unknown: it went to SharePoint groups Ask HR couldn't list.
  state: VisibilityState;
  reach: number; // people in Ask HR - Users who can open it
  of: number; // people in Ask HR - Users
  who: string[]; // who has access as SharePoint lists it, broadest first
  link?: "view" | "edit";
  unresolved?: string[]; // SharePoint groups whose members Ask HR couldn't read
  // Where the permissions are set: "" on the item itself, "/" the library, else a folder path.
  setOn: string;
  checkedAt: string;
}

// A source folder: its own access, and a count of its documents in each state.
export interface SourceVisibility extends Visibility {
  docs: Record<VisibilityState, number>;
}

export const VISIBILITY_LABELS: Record<VisibilityState, string> = {
  everyone: "Everyone",
  link: "Org link only",
  some: "Some people",
  unknown: "Can't tell",
};

// A group covering this share of Ask HR - Users counts as everyone: an all-company group misses
// a few new starters and service accounts, and that shouldn't flag every document.
export const EVERYONE_SHARE = 0.95;

// Worst first: the order the Employees column sorts in.
export const VISIBILITY_ORDER: Record<VisibilityState, number> = { some: 0, link: 1, unknown: 2, everyone: 3 };

export const worstState = (states: VisibilityState[]): VisibilityState | undefined =>
  states.length ? states.reduce((a, b) => (VISIBILITY_ORDER[b] < VISIBILITY_ORDER[a] ? b : a)) : undefined;

export const emptyCounts = (): Record<VisibilityState, number> => ({ everyone: 0, link: 0, some: 0, unknown: 0 });

// A source shows the worst of its folder and its documents, so it never looks fine while it holds
// a file most employees can't open. Undefined: not checked yet (or a website).
export function sourceState(v: SourceVisibility | undefined): VisibilityState | undefined {
  if (!v) return undefined;
  return worstState([v.state, ...(Object.keys(v.docs) as VisibilityState[]).filter((s) => v.docs[s] > 0)]);
}

// Most employees can't open it: what the Employees filter shows and the alert counts.
export const isHidden = (v: Pick<Visibility, "state"> | undefined): boolean => v?.state === "some" || v?.state === "link";

// What Admin says about where the permissions come from.
export function setOnText(setOn: string): string {
  if (setOn === "") return "It has its own permissions, not its folder's.";
  if (setOn === "/") return "It inherits the library's permissions.";
  return `It inherits the permissions set on the folder ${setOn}.`;
}
